import { useCallback } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { useModelStore } from '@editorStores'
import { useSelectionMode } from '@editorHooks'

export const ADD_COMMENTS_SELECTION_KEY = 'add-comments'

/**
 * Shared wiring for the "add comment" tool: toggles the `add-comments` selection
 * mode (the actual point pick + comment creation is handled once by
 * `CommentsController` in the scene) and makes comments visible. Used by the
 * merged `CommentToolSelect` dropdown in each editor toolbar (structural
 * planning, results, global loads).
 *
 * Also registers the "t" shortcut to toggle the mode. Because exactly one editor
 * toolbar (and therefore one instance of this hook) is mounted per route, the
 * shortcut is available on every page that exposes the tool without duplicate
 * registrations.
 */
const useAddCommentTool = (): { isActive: boolean; toggle: () => void } => {
  const setShowComments = useModelStore(state => state.setShowComments)
  const { selectionModeKey, setSelectionMode, unsetSelectionMode } = useSelectionMode()

  const isActive = selectionModeKey === ADD_COMMENTS_SELECTION_KEY

  const toggle = useCallback(() => {
    if (isActive) {
      unsetSelectionMode()
      return
    }
    setShowComments(true)
    setSelectionMode({
      key: ADD_COMMENTS_SELECTION_KEY,
      message: 'Punkt für Kommentar anwählen',
    })
  }, [isActive, setShowComments, setSelectionMode, unsetSelectionMode])

  useHotkeys(['t'], () => toggle(), [toggle])

  return { isActive, toggle }
}

export default useAddCommentTool
